import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type WizardMode = "analysis" | "angles";
type ItemType = "product" | "offering";

type StepState = "pending" | "active" | "completed" | "held_final";

interface StepConfig {
  label: string;
  subtitle: string;
  startTime: number;
}

const PRODUCT_ANALYSIS_STEPS: StepConfig[] = [
  { label: "Initializing analysis", subtitle: "Preparing analysis context", startTime: 0 },
  { label: "Reading persona drivers", subtitle: "Extracting high-signal persona traits", startTime: 12 },
  { label: "Reading product truths", subtitle: "Extracting product truths and proof", startTime: 32 },
  { label: "Mapping intersections", subtitle: "Mapping narrative intersections", startTime: 52 },
  { label: "Ranking opportunities", subtitle: "Scoring and ranking opportunities", startTime: 82 },
  { label: "Building readiness assessment", subtitle: "Assessing readiness and proof gaps", startTime: 112 },
  { label: "Analysis in progress", subtitle: "Waiting for the provider response", startTime: 142 },
];

const OFFERING_ANALYSIS_STEPS: StepConfig[] = [
  { label: "Initializing analysis", subtitle: "Preparing analysis context", startTime: 0 },
  { label: "Reading persona drivers", subtitle: "Extracting high-signal persona traits", startTime: 12 },
  { label: "Reading offering essentials", subtitle: "Extracting value, deliverables and proof", startTime: 32 },
  { label: "Mapping fit intersections", subtitle: "Mapping persona–offering fit", startTime: 52 },
  { label: "Ranking opportunities", subtitle: "Scoring and ranking opportunities", startTime: 82 },
  { label: "Building readiness assessment", subtitle: "Assessing readiness and trust gaps", startTime: 112 },
  { label: "Analysis in progress", subtitle: "Waiting for the provider response", startTime: 142 },
];

const ANGLE_STEPS: StepConfig[] = [
  { label: "Initializing narrative engine", subtitle: "Preparing narrative generation context", startTime: 0 },
  { label: "Loading selected opportunities", subtitle: "Loading selected opportunities", startTime: 10 },
  { label: "Expanding narrative directions", subtitle: "Expanding narrative branches", startTime: 25 },
  { label: "Building angle structures", subtitle: "Structuring narrative angles", startTime: 50 },
  { label: "Stress-testing distinctiveness", subtitle: "Comparing and separating angle directions", startTime: 80 },
  { label: "Building proof anchors", subtitle: "Attaching proof architecture", startTime: 110 },
  { label: "Generating angle stems", subtitle: "Generating campaign stems", startTime: 135 },
  { label: "Finalizing narrative angles", subtitle: "Packaging final narrative angles", startTime: 160 },
];

function getElapsedSeconds(startedAt?: string | Date | null): number {
  if (!startedAt) return 0;
  const start = new Date(startedAt).getTime();
  return Math.max(0, (Date.now() - start) / 1000);
}

function getCurrentStepIndex(elapsed: number, steps: StepConfig[]): number {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (elapsed >= steps[i].startTime) return i;
  }
  return 0;
}

function getStepState(index: number, currentIndex: number, isLastStep: boolean): StepState {
  if (index < currentIndex) return "completed";
  if (index === currentIndex) return isLastStep ? "held_final" : "active";
  return "pending";
}

interface GenerationWizardProps {
  mode: WizardMode;
  isRunning: boolean;
  hasFailed?: boolean;
  persistedStatus?: "queued" | "running" | "completed" | "failed";
  errorMessage?: string | null;
  startedAt?: string | Date | null;
  title?: string;
  subtitle?: string;
  onRetry?: () => void;
  itemType?: ItemType;
}

export function GenerationWizard({
  mode,
  isRunning,
  hasFailed = false,
  persistedStatus,
  errorMessage,
  startedAt,
  title,
  subtitle,
  onRetry,
  itemType = "product",
}: GenerationWizardProps) {
  const steps = mode === "analysis"
    ? (itemType === "offering" ? OFFERING_ANALYSIS_STEPS : PRODUCT_ANALYSIS_STEPS)
    : ANGLE_STEPS;
  const [elapsed, setElapsed] = useState(() => getElapsedSeconds(startedAt));
  const [wasRunning, setWasRunning] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    if (isRunning) {
      setWasRunning(true);
      setShowComplete(false);
    } else if (wasRunning && !hasFailed) {
      setShowComplete(true);
      const t = setTimeout(() => {
        setShowComplete(false);
        setWasRunning(false);
      }, 500);
      return () => clearTimeout(t);
    }
  }, [isRunning, hasFailed, wasRunning]);

  useEffect(() => {
    setElapsed(getElapsedSeconds(startedAt));
  }, [startedAt]);

  useEffect(() => {
    if (!isRunning && !hasFailed) return;
    const interval = setInterval(() => {
      setElapsed(getElapsedSeconds(startedAt));
    }, 500);
    return () => clearInterval(interval);
  }, [isRunning, hasFailed, startedAt]);

  const currentIndex = persistedStatus === "queued" ? 0 : getCurrentStepIndex(elapsed, steps);
  const isLastStep = currentIndex === steps.length - 1;

  const defaultTitle = mode === "analysis"
    ? (itemType === "offering" ? "Persona-Offering Analysis" : "Persona-Product Analysis")
    : "Narrative Angle Generation";

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  if (hasFailed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-gradient-to-br from-red-50/60 to-white dark:from-red-950/20 dark:to-background p-8"
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold tracking-tight mb-1" data-testid="text-wizard-failed">Generation Failed</h3>
            <p className="text-sm text-muted-foreground" data-testid="text-wizard-error">
              {errorMessage || "Something went wrong during the process. You can retry the generation."}
            </p>
          </div>
          {onRetry && (
            <Button onClick={onRetry} variant="outline" className="rounded-full" data-testid="button-wizard-retry">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          )}
        </div>
      </motion.div>
    );
  }

  if (showComplete) {
    return (
      <motion.div
        initial={{ opacity: 1, scale: 1 }}
        animate={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="rounded-2xl border p-8 text-center"
      >
        <div className="flex items-center justify-center gap-2">
          <Check className="h-5 w-5 text-emerald-500" />
          <span className="text-sm font-medium text-emerald-600">Complete</span>
        </div>
      </motion.div>
    );
  }

  if (!isRunning && !hasFailed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/[0.02] via-white to-primary/[0.01] dark:from-primary/[0.04] dark:via-background dark:to-primary/[0.02] overflow-hidden"
      data-testid="generation-wizard"
    >
      <div className="px-6 pt-5 pb-4 border-b border-primary/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight" data-testid="text-wizard-title">{title || defaultTitle}</h2>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {persistedStatus !== "queued" && <span className="text-xs text-muted-foreground/60 tabular-nums font-mono" data-testid="text-wizard-elapsed">{formatTime(elapsed)}</span>}
            <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] font-semibold rounded-full px-2.5 animate-pulse" data-testid="badge-wizard-running">
              {persistedStatus === "queued" ? "Queued" : "Running"}
            </Badge>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row min-h-[280px] md:min-h-[320px]">
        <StepRail steps={steps} currentIndex={currentIndex} />
        <StagePanel mode={mode} itemType={itemType} currentIndex={currentIndex} isLastStep={isLastStep} subtitle={steps[currentIndex].subtitle} />
      </div>
    </motion.div>
  );
}

function StepRail({ steps, currentIndex }: { steps: StepConfig[]; currentIndex: number }) {
  return (
    <div className="w-full md:w-[260px] shrink-0 border-b md:border-b-0 md:border-r border-primary/10 py-4 px-5 space-y-0" data-testid="wizard-step-rail">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const state = getStepState(i, currentIndex, isLast && currentIndex === i);
        return (
          <StepNode key={i} step={step} state={state} index={i} isLast={i === steps.length - 1} />
        );
      })}
    </div>
  );
}

function StepNode({ step, state, index, isLast }: { step: StepConfig; state: StepState; index: number; isLast: boolean }) {
  return (
    <div className="flex items-start gap-3 relative" data-testid={`wizard-step-${index}`}>
      <div className="flex flex-col items-center">
        <div className="relative flex items-center justify-center h-6 w-6">
          {state === "completed" && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center"
            >
              <Check className="h-3 w-3 text-primary" />
            </motion.div>
          )}
          {state === "active" && (
            <div className="relative">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="h-3 w-3 rounded-full bg-primary shadow-[0_0_8px_rgba(124,58,237,0.4)]"
              />
            </div>
          )}
          {state === "held_final" && (
            <div className="relative">
              <motion.div
                animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 m-auto h-5 w-5 rounded-full border-2 border-primary/40"
                style={{ top: "-4px", left: "-4px", right: "-4px", bottom: "-4px" }}
              />
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="h-3 w-3 rounded-full bg-primary shadow-[0_0_12px_rgba(124,58,237,0.5)]"
              />
            </div>
          )}
          {state === "pending" && (
            <div className="h-3 w-3 rounded-full border-2 border-muted-foreground/20" />
          )}
        </div>
        {!isLast && (
          <div className={`w-px h-6 mt-1 ${state === "completed" || state === "active" || state === "held_final" ? "bg-primary/20" : "bg-border/50"}`} />
        )}
      </div>
      <div className="pb-5 pt-0.5">
        <span className={`text-[12px] font-medium leading-tight block ${
          state === "pending" ? "text-muted-foreground/40" :
          state === "completed" ? "text-foreground/60" :
          "text-foreground"
        }`}>
          {step.label}
        </span>
        {(state === "active" || state === "held_final") && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            transition={{ duration: 0.8 }}
            className="h-[2px] bg-gradient-to-r from-primary/40 to-primary/10 rounded-full mt-1.5 max-w-[140px] overflow-hidden relative"
          >
            <motion.div
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-primary/60 to-transparent"
            />
          </motion.div>
        )}
      </div>
    </div>
  );
}

function StagePanel({ mode, itemType, currentIndex, isLastStep, subtitle }: {
  mode: WizardMode;
  itemType: ItemType;
  currentIndex: number;
  isLastStep: boolean;
  subtitle: string;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 relative overflow-hidden" data-testid="wizard-stage-panel">
      <GridBackground />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 flex flex-col items-center w-full max-w-[360px]"
        >
          {mode === "analysis" ? (
            <AnalysisStageVisual step={currentIndex} itemType={itemType} />
          ) : (
            <AngleStageVisual step={currentIndex} itemType={itemType} />
          )}

          <div className="mt-5 text-center">
            <p className="text-[12px] font-medium text-foreground/70">{subtitle}</p>
            {isLastStep && <AnimatedDots />}
          </div>

          <MicroMessages step={currentIndex} mode={mode} itemType={itemType} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function GridBackground() {
  return (
    <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)",
      backgroundSize: "24px 24px",
    }} />
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex gap-0.5 mt-1.5">
      {[0, 1, 2].map(i => (
        <motion.span
          key={i}
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
          className="h-1 w-1 rounded-full bg-primary/60 inline-block"
        />
      ))}
    </span>
  );
}

const MICRO_MESSAGES: Record<string, string[]> = {
  "analysis-0": ["Initializing context graph"],
  "analysis-1": ["Evaluating belief structures", "Scanning emotional triggers"],
  "analysis-2": ["Identifying proof patterns", "Comparing mechanism strength"],
  "analysis-3": ["Evaluating signal strength", "Comparing tension patterns"],
  "analysis-4": ["Scoring narrative potential", "Ranking by resonance"],
  "analysis-5": ["Checking proof coverage", "Identifying gaps"],
  "analysis-6": ["Waiting for persisted completion status"],
  "offering-analysis-0": ["Initializing context graph"],
  "offering-analysis-1": ["Evaluating belief structures", "Scanning emotional triggers"],
  "offering-analysis-2": ["Mapping deliverables and outcomes", "Comparing value drivers"],
  "offering-analysis-3": ["Evaluating fit signals", "Comparing trust patterns"],
  "offering-analysis-4": ["Scoring narrative potential", "Ranking by resonance"],
  "offering-analysis-5": ["Checking trust coverage", "Identifying objection gaps"],
  "offering-analysis-6": ["Waiting for persisted completion status"],
  "angles-0": ["Preparing engine context"],
  "angles-1": ["Loading opportunity data"],
  "angles-2": ["Branching narrative paths", "Exploring directions"],
  "angles-3": ["Structuring angle frames", "Resolving narrative structure"],
  "angles-4": ["Testing distinctiveness", "Separating angle vectors"],
  "angles-5": ["Attaching proof chains", "Resolving anchor patterns"],
  "angles-6": ["Extending campaign stems", "Building stem architecture"],
  "angles-7": ["Packaging final output", "Assembling structured output"],
};

function MicroMessages({ step, mode, itemType }: { step: number; mode: WizardMode; itemType: ItemType }) {
  const modeKey = mode === "analysis" && itemType === "offering" ? "offering-analysis" : mode;
  const key = `${modeKey}-${step}`;
  const messages = MICRO_MESSAGES[key] || [];
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    if (messages.length <= 1) return;
    const t = setInterval(() => setMsgIndex(i => (i + 1) % messages.length), 4000);
    return () => clearInterval(t);
  }, [messages.length]);

  if (messages.length === 0) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.p
        key={`${key}-${msgIndex}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="text-[10px] text-muted-foreground/40 mt-2 italic"
      >
        {messages[msgIndex]}
      </motion.p>
    </AnimatePresence>
  );
}

function GlowingNode({ delay = 0, size = 10, color = "bg-primary" }: { delay?: number; size?: number; color?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5, ease: "easeOut" }}
      className="relative"
    >
      <motion.div
        animate={{ scale: [1, 1.3, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay }}
        className={`rounded-full ${color} shadow-[0_0_8px_rgba(124,58,237,0.3)]`}
        style={{ width: size, height: size }}
      />
    </motion.div>
  );
}

function ConnectorLine({ delay = 0, width = 40, vertical = false }: { delay?: number; width?: number; vertical?: boolean }) {
  return (
    <motion.div
      initial={{ [vertical ? "height" : "width"]: 0, opacity: 0 }}
      animate={{ [vertical ? "height" : "width"]: width, opacity: 1 }}
      transition={{ delay, duration: 0.6, ease: "easeOut" }}
      className="bg-gradient-to-r from-primary/20 to-primary/5 rounded-full"
      style={vertical ? { width: 2, height: width } : { height: 2, width }}
    />
  );
}

function SignalChip({ label, delay, color = "bg-primary/8 text-primary border-primary/15" }: { label: string; delay: number; color?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.4, ease: "easeOut" }}
      className={`px-3 py-1.5 rounded-full text-[10px] font-medium border ${color}`}
    >
      {label}
    </motion.div>
  );
}

function ShimmerCard({ delay = 0, className = "" }: { delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className={`relative rounded-xl border border-primary/10 bg-white/80 dark:bg-background/80 overflow-hidden ${className}`}
    >
      <motion.div
        animate={{ x: ["-100%", "200%"] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: delay + 1 }}
        className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-primary/[0.06] to-transparent"
      />
      <div className="relative z-10 p-3">
        <div className="h-2 w-3/4 bg-primary/8 rounded-full mb-2" />
        <div className="h-1.5 w-1/2 bg-muted/30 rounded-full" />
      </div>
    </motion.div>
  );
}

function FinalCard({ children }: { children?: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative rounded-2xl border border-primary/15 bg-white/90 dark:bg-background/90 p-5 w-full max-w-[280px] overflow-hidden"
    >
      <motion.div
        animate={{ x: ["-100%", "200%"] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 w-1/4 bg-gradient-to-r from-transparent via-primary/[0.06] to-transparent"
      />
      <InwardParticles />
      <div className="relative z-10">
        {children || (
          <>
            <div className="h-2.5 w-2/3 bg-primary/12 rounded-full mb-3" />
            <div className="h-2 w-full bg-muted/20 rounded-full mb-2" />
            <div className="h-2 w-4/5 bg-muted/15 rounded-full mb-2" />
            <div className="h-2 w-3/5 bg-muted/10 rounded-full" />
          </>
        )}
      </div>
    </motion.div>
  );
}

function InwardParticles() {
  const particles = useMemo(() => 
    Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      angle: (i / 6) * 360,
      delay: i * 0.5,
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => {
        const rad = (p.angle * Math.PI) / 180;
        const startX = Math.cos(rad) * 120;
        const startY = Math.sin(rad) * 120;
        return (
          <motion.div
            key={p.id}
            animate={{
              x: [startX, 0],
              y: [startY, 0],
              opacity: [0, 0.6, 0],
            }}
            transition={{ duration: 3, repeat: Infinity, delay: p.delay, ease: "easeInOut" }}
            className="absolute top-1/2 left-1/2 h-1 w-1 rounded-full bg-primary/40"
          />
        );
      })}
    </div>
  );
}

function AnalysisStageVisual({ step, itemType }: { step: number; itemType: ItemType }) {
  switch (step) {
    case 0:
      return (
        <div className="flex items-center gap-4">
          <GlowingNode delay={0} size={12} />
          <ConnectorLine delay={0.4} width={40} />
          <GlowingNode delay={0.6} size={10} />
          <ConnectorLine delay={1} width={40} />
          <GlowingNode delay={1.2} size={12} />
        </div>
      );

    case 1: {
      const chips = ["Beliefs", "Fears", "Identity", "Motivations", "Trust Signals"];
      return (
        <div className="flex flex-wrap gap-2 justify-center max-w-[300px]">
          {chips.map((c, i) => (
            <SignalChip key={c} label={c} delay={i * 0.3} color="bg-violet-50 text-violet-600 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800" />
          ))}
        </div>
      );
    }

    case 2: {
      const chips = itemType === "offering"
        ? ["Deliverables", "Outcomes", "Process", "Proof", "Pricing"]
        : ["Ingredients", "Mechanisms", "Differentiators", "Proof Points", "Use Cases"];
      return (
        <div className="flex flex-wrap gap-2 justify-center max-w-[300px]">
          {chips.map((c, i) => (
            <SignalChip key={c} label={c} delay={i * 0.3} color="bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800" />
          ))}
        </div>
      );
    }

    case 3:
      return <IntersectionVisual itemType={itemType} />;

    case 4:
      return <RankingVisual />;

    case 5:
      return <ReadinessVisual />;

    case 6:
      return <FinalCard />;

    default:
      return null;
  }
}

function AngleStageVisual({ step, itemType }: { step: number; itemType: ItemType }) {
  switch (step) {
    case 0:
      return (
        <div className="flex items-center gap-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3">
              <GlowingNode delay={i * 0.3} size={10} />
              {i < 3 && <ConnectorLine delay={i * 0.3 + 0.2} width={30} />}
            </div>
          ))}
        </div>
      );

    case 1:
      return (
        <div className="space-y-2 w-full max-w-[240px]">
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.4, duration: 0.5 }}
              className="rounded-lg border border-primary/10 bg-primary/[0.02] p-2.5 flex items-center gap-2"
            >
              <div className="h-2 w-2 rounded-full bg-primary/30" />
              <div className="h-1.5 flex-1 bg-primary/8 rounded-full" />
            </motion.div>
          ))}
        </div>
      );

    case 2:
      return <BranchingVisual />;

    case 3:
      return (
        <div className="space-y-2.5 w-full max-w-[260px]">
          {["Summary", "Tension", "Belief Shift", "Promise"].map((label, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.4, duration: 0.4 }}
              className="rounded-lg border border-primary/10 p-2.5"
            >
              <div className="text-[9px] font-semibold uppercase tracking-wider text-primary/40 mb-1">{label}</div>
              <div className="h-1.5 w-3/4 bg-muted/20 rounded-full" />
            </motion.div>
          ))}
        </div>
      );

    case 4:
      return <DistinctivenessVisual />;

    case 5: {
      const proofs = itemType === "offering"
        ? ["Credentials", "Case Studies", "Testimonials", "Guarantees"]
        : ["Sourcing", "Ingredients", "Mechanism", "Category Contrast"];
      return (
        <div className="space-y-2 w-full max-w-[240px]">
          <div className="rounded-xl border border-primary/10 p-3 space-y-2">
            {proofs.map((p, i) => (
              <motion.div
                key={p}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.4, duration: 0.4 }}
                className="flex items-center gap-2"
              >
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500/50" />
                <span className="text-[10px] text-foreground/50">{p}</span>
              </motion.div>
            ))}
          </div>
        </div>
      );
    }

    case 6:
      return (
        <div className="space-y-2 w-full max-w-[240px]">
          <div className="rounded-xl border border-primary/10 p-3">
            <div className="h-2 w-1/2 bg-primary/10 rounded-full mb-3" />
            {[0, 1, 2].map(i => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.5, duration: 0.4 }}
                className="flex items-center gap-2 mb-1.5"
              >
                <div className="w-px h-3 bg-primary/20" />
                <div className="h-1.5 flex-1 bg-muted/15 rounded-full" />
              </motion.div>
            ))}
          </div>
        </div>
      );

    case 7:
      return (
        <div className="flex gap-2 w-full max-w-[300px] justify-center">
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.3, duration: 0.5 }}
              className="relative rounded-xl border border-primary/15 bg-white/90 dark:bg-background/90 p-3 flex-1 overflow-hidden"
            >
              <motion.div
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: i * 1.2 }}
                className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-primary/[0.06] to-transparent"
              />
              <div className="relative z-10">
                <div className="h-2 w-3/4 bg-primary/10 rounded-full mb-2" />
                <div className="h-1.5 w-full bg-muted/15 rounded-full mb-1.5" />
                <div className="h-1.5 w-2/3 bg-muted/10 rounded-full" />
              </div>
            </motion.div>
          ))}
          <InwardParticles />
        </div>
      );

    default:
      return null;
  }
}

function IntersectionVisual({ itemType }: { itemType: ItemType }) {
  const left = ["Beliefs", "Fears", "Trust"];
  const right = itemType === "offering"
    ? ["Outcomes", "Proof", "Process"]
    : ["Ingredients", "Proof", "Mechanism"];
  const connections = [
    { from: 0, to: 1, delay: 0.6 },
    { from: 1, to: 0, delay: 1 },
    { from: 0, to: 2, delay: 1.4 },
    { from: 2, to: 1, delay: 1.8 },
  ];

  return (
    <div className="relative w-full max-w-[300px] h-[120px]">
      <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-2">
        {left.map((l, i) => (
          <motion.div
            key={l}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.15 }}
            className="px-2.5 py-1 rounded-full text-[9px] font-medium bg-violet-50 text-violet-600 border border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800"
          >
            {l}
          </motion.div>
        ))}
      </div>
      <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-between py-2">
        {right.map((r, i) => (
          <motion.div
            key={r}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.15 + 0.3 }}
            className="px-2.5 py-1 rounded-full text-[9px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
          >
            {r}
          </motion.div>
        ))}
      </div>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 120" fill="none">
        {connections.map((c, i) => {
          const y1 = 15 + c.from * 42;
          const y2 = 15 + c.to * 42;
          return (
            <motion.line
              key={i}
              x1={80}
              y1={y1}
              x2={220}
              y2={y2}
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              strokeOpacity={0.2}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: c.delay, duration: 0.6 }}
            />
          );
        })}
      </svg>
    </div>
  );
}

function RankingVisual() {
  const items = useMemo(() => [
    { label: "Opportunity A", score: 9 },
    { label: "Opportunity B", score: 7 },
    { label: "Opportunity C", score: 8 },
    { label: "Opportunity D", score: 6 },
    { label: "Opportunity E", score: 5 },
  ], []);

  const sorted = useMemo(() =>
    [...items].sort((a, b) => b.score - a.score),
    [items]
  );

  return (
    <div className="space-y-1.5 w-full max-w-[240px]">
      {sorted.map((item, i) => (
        <motion.div
          key={item.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.25, duration: 0.5, ease: "easeOut" }}
          className="flex items-center gap-2.5 rounded-lg border border-primary/10 p-2 bg-white/50 dark:bg-background/50"
        >
          <motion.div
            animate={i === 0 ? { boxShadow: ["0 0 0px rgba(124,58,237,0)", "0 0 8px rgba(124,58,237,0.3)", "0 0 0px rgba(124,58,237,0)"] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
            className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary"
          >
            {item.score}
          </motion.div>
          <div className="h-1.5 flex-1 bg-muted/15 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${item.score * 10}%` }}
              transition={{ delay: i * 0.25 + 0.3, duration: 0.6 }}
              className="h-full bg-primary/20 rounded-full"
            />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ReadinessVisual() {
  const [count, setCount] = useState(0);
  const target = 82;

  useEffect(() => {
    const duration = 2000;
    const start = Date.now();
    let raf: number;
    const tick = () => {
      const progress = Math.min((Date.now() - start) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="flex gap-2.5 w-full max-w-[300px]">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0 }}
        className="flex-1 rounded-xl border border-emerald-200 dark:border-emerald-800 p-3"
      >
        <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-500/60 mb-2">Strengths</div>
        {[0, 1].map(i => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 + i * 0.3 }}
            className="h-1.5 w-3/4 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-1.5"
          />
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex-1 rounded-xl border border-amber-200 dark:border-amber-800 p-3"
      >
        <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-500/60 mb-2">Warnings</div>
        {[0, 1].map(i => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 + i * 0.3 }}
            className="h-1.5 w-3/4 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-1.5"
          />
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4 }}
        className="w-[80px] rounded-xl border border-primary/15 p-3 flex flex-col items-center justify-center"
      >
        <div className="text-[9px] font-semibold uppercase tracking-wider text-primary/40 mb-1">Score</div>
        <span className="text-2xl font-bold tabular-nums text-primary">{count}</span>
      </motion.div>
    </div>
  );
}

function BranchingVisual() {
  return (
    <div className="relative w-full max-w-[280px] h-[140px]">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center"
      >
        <div className="h-3 w-3 rounded-full bg-primary/30" />
      </motion.div>

      {[
        { x: -80, y: -40, delay: 0.4 },
        { x: 80, y: -40, delay: 0.6 },
        { x: -60, y: 40, delay: 0.8 },
        { x: 60, y: 40, delay: 1.0 },
        { x: 0, y: -50, delay: 1.2 },
      ].map((node, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: node.delay, duration: 0.4 }}
          className="absolute"
          style={{
            left: `calc(50% + ${node.x}px)`,
            top: `calc(50% + ${node.y}px)`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, delay: node.delay }}
            className="h-2.5 w-2.5 rounded-full bg-primary/30 shadow-[0_0_6px_rgba(124,58,237,0.2)]"
          />
        </motion.div>
      ))}
    </div>
  );
}

function DistinctivenessVisual() {
  return (
    <div className="flex gap-3 w-full max-w-[280px]">
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 0 }}
          animate={{
            opacity: 1,
            x: (i - 1) * 8,
            scale: i === 1 ? 1.02 : 0.98,
          }}
          transition={{ delay: 0.3 + i * 0.2, duration: 0.6 }}
          className={`flex-1 rounded-xl border p-2.5 ${
            i === 1
              ? "border-primary/20 bg-primary/[0.03] shadow-sm"
              : "border-border/40 bg-white/50 dark:bg-background/50"
          }`}
        >
          <div className={`h-2 w-2/3 rounded-full mb-2 ${i === 1 ? "bg-primary/15" : "bg-muted/20"}`} />
          <div className="h-1.5 w-full bg-muted/10 rounded-full mb-1" />
          <div className="h-1.5 w-3/4 bg-muted/8 rounded-full" />
        </motion.div>
      ))}
    </div>
  );
}
